import React, { useState } from "react";
import { useMutation } from "react-apollo";
import {
  Box,
  Divider,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Modal,
  Heading,
  ModalOverlay,
  ModalContent,
  ModalFooter,
  ModalBody,
  Button,
  Alert,
  AlertIcon,
  Text,
  InputRightElement,
  InputGroup,
  Link,
} from "@chakra-ui/core";
import { FaEyeSlash, FaEye } from "react-icons/fa";
import { Redirect } from "react-router-dom";
import { FiLogIn } from "react-icons/fi";
import { CREATE_MERCHANT, LOGIN_MUTATION } from "../../graphql/mutations";

/** This compoenent handle the Signup and Login form
 * that can be called from the Landing page
 * The `intend` prop is to enforce the Login option
 * without givin user the ability to switch to sign-up
 * This use case is for when a user try to go to Dashboard without
 * being logged in
 */

const Signup = ({ isOpen, onClose, intend }) => {
  const [createMerchant] = useMutation(CREATE_MERCHANT);
  const [loginMerchant] = useMutation(LOGIN_MUTATION);
  const [formData, setFormData] = useState({});
  const [error, setError] = useState("");
  const [finalMessage, setFinalMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [redirect, setRedirect] = useState(false);
  const [show, setShow] = React.useState(false);
  const [action, setAction] = React.useState(intend || "signup");
  const handleClick = () => setShow(!show);

  const onSubmit = (event) => {
    event.preventDefault();
    /**
     * signupUser(email: string, password: string, data: Object, directLogin: boolean = true)
      directLogin will internally set state to the newly created user
      isConfirmedUser will be false
      isLoggedIn will be true
      setting directLogin to false won't trigger the state
     */

    const { businessName, firstName, lastName, email, password } = formData;

    if (action === "signup") {
      setIsLoading(true);
      createMerchant({
        variables: {
          input: { businessName, firstName, lastName, email, password },
        },
      })
        .then((res) => {
          setFinalMessage(
            `You have successfully signed up. Please verify your email ${email} to validate your email address to continue`
          );
          setIsSuccess(true);
          console.log("Success");
        })
        .catch((err) => {
          setFinalMessage(`Something went wrong`);
          const errorStack = err.message.split(":");
          setError(errorStack[errorStack.length - 1]);
          setIsLoading(false);
        });
    } else if (action === "login") {
      setIsLoading(true);
      loginMerchant({ variables: { email, password } })
        .then((res) => {
          localStorage.setItem("token", res.data.loginMerchant.token);
          setRedirect(true)
        })
        .catch((err) => {
          setFinalMessage(`Something went wrong`);
          const errorStack = err.message.split(":");
          setError(errorStack[errorStack.length - 1]);
          setIsLoading(false);
        });
    }
  };

  const handleChange = ({ target: { name, value } }) => {
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  if (redirect) {
    return <Redirect to="/dashboard" />;
  }

  return (
    <Modal closeOnOverlayClick={false} isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <form onSubmit={onSubmit}>
        <ModalContent py="20px" borderRadius="5px">
          <ModalBody>
            <Box mb="25px">
              <Heading as="h3" size="lg">
                <Stack
                  my="3"
                  color="primary.600"
                  isInline
                  justifyContent="space-around"
                >
                  {/* If the intial intend is to call the Login we do not give the option to swap 
                  between signup and login --> only login */}
                  {intend !== "login" ? (
                    <>
                      <Link
                        _hover={{ outline: "none", color: "success.500" }}
                        onClick={() => {
                          setFinalMessage("");
                          setError("");
                          setAction("signup");
                        }}
                        color={
                          action === "signup" ? "success.500" : "primary.200"
                        }
                        textTransform="uppercase"
                      >
                        Sign-up
                      </Link>
                      <Link
                        _hover={{ outline: "none", color: "success.500" }}
                        onClick={() => {
                          setFinalMessage("");
                          setError("");
                          setAction("login");
                        }}
                        color={
                          action === "login" ? "success.500" : "primary.200"
                        }
                        textTransform="uppercase"
                      >
                        Login
                      </Link>
                    </>
                  ) : (
                    <Heading as="h3" size="lg">
                      Login
                    </Heading>
                  )}
                </Stack>
              </Heading>
            </Box>
            <Divider my="4" />
            {finalMessage && (
              <Alert
                borderRadius="5px"
                my="40px"
                status={error ? "error" : "success"}
              >
                <AlertIcon />
                <Stack>
                  <Text fontWeight={error ? "bold" : "normal"}>
                    {finalMessage}
                  </Text>
                  <Text>{error && error}</Text>
                </Stack>
              </Alert>
            )}
            {!isSuccess && (
              <FormControl isRequired>
                {action === "signup" && (
                  <Stack mb={4} spacing={8}>
                    <Box>
                      <FormLabel htmlFor="First Name" textTransform="uppercase">
                        Personal information
                      </FormLabel>
                      <Stack isInline>
                        <Input
                          name="firstName"
                          focusBorderColor="success.500"
                          id="first-name"
                          type="text"
                          placeholder="First name"
                          aria-describedby="User first name"
                          onChange={handleChange}
                        />
                        <Input
                          name="lastName"
                          focusBorderColor="success.500"
                          id="last-name"
                          type="text"
                          placeholder="Last name"
                          aria-describedby="User last name"
                          onChange={handleChange}
                        />
                      </Stack>
                    </Box>
                    <Box>
                      <FormLabel
                        htmlFor="Business Name"
                        textTransform="uppercase"
                      >
                        Business information
                      </FormLabel>
                      <Input
                        name="businessName"
                        focusBorderColor="success.500"
                        id="business-name"
                        type="text"
                        placeholder="Company name"
                        aria-describedby="User business name"
                        onChange={handleChange}
                      />
                    </Box>
                  </Stack>
                )}
                <Stack spacing={4}>
                  <Box>
                    <Input
                      name="email"
                      focusBorderColor="success.500"
                      id="email"
                      type="email"
                      placeholder="Business email address"
                      aria-describedby="User email address field"
                      onChange={handleChange}
                    />
                  </Box>
                  <Box>
                    <InputGroup size="md">
                      <Input
                        focusBorderColor="success.500"
                        name="password"
                        id="password"
                        aria-describedby="User password field"
                        pr="4.5rem"
                        type={show ? "text" : "password"}
                        placeholder="Password"
                        onChange={handleChange}
                      />
                      <InputRightElement width="4.5rem">
                        <Button h="1.75rem" size="sm" onClick={handleClick}>
                          <Box as={show ? FaEye : FaEyeSlash} />
                        </Button>
                      </InputRightElement>
                    </InputGroup>
                  </Box>
                </Stack>
              </FormControl>
            )}
          </ModalBody>
          <ModalFooter>
            {isSuccess && (
              <Button
                onClick={() => {
                  onClose();
                }}
              >
                Close
              </Button>
            )}
            {!isSuccess && (
              <>
                <Button
                  color="#FFFFFF"
                  bg="success.500"
                  _hover={{ background: "#0F8D45" }}
                  mr={3}
                  type="submit"
                  isLoading={isLoading}
                  loadingText="Submitting"
                  leftIcon={FiLogIn}
                >
                  {action === "signup" ? "Sign-up" : "Login"}
                </Button>
                {intend !== "login" && (
                  <Button
                    isDisabled={isLoading}
                    onClick={() => {
                      setFinalMessage("");
                      setError("");
                      onClose();
                    }}
                  >
                    Cancel
                  </Button>
                )}
              </>
            )}
          </ModalFooter>
        </ModalContent>
      </form>
    </Modal>
  );
};

export default Signup;
